import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Alert,
    CircularProgress,
    Paper,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField
} from '@mui/material';
import {
    LocalLibrary,
    FileCopy,
    Visibility,
    MoreVert,
    ArrowBack,
    AutoAwesome
} from '@mui/icons-material';
import { GenerateSchemaDialog } from './GenerateSchemaDialog';
import { useUser } from '../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { handleFirestoreError } from '../../utils/errorHandling';
import {
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
    addDoc,
    Timestamp,
    Unsubscribe
} from 'firebase/firestore';
import { db } from '../../firebaseClient';
import { EvaluationSchema } from './EvaluationSchemaList';

const GlobalSchemaLibrary: React.FC = () => {
    const { user, userProfile, isTenantAdmin, isGlobalAdmin } = useUser();
    useDocumentTitle('Global Framework Library');
    const navigate = useNavigate();

    const [globalSchemas, setGlobalSchemas] = useState<EvaluationSchema[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [selectedSchema, setSelectedSchema] = useState<EvaluationSchema | null>(null);
    const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
    const [schemaToClone, setSchemaToClone] = useState<EvaluationSchema | null>(null);
    const [cloneName, setCloneName] = useState('');
    const [cloning, setCloning] = useState(false);
    const [generateDialogOpen, setGenerateDialogOpen] = useState(false);

    useEffect(() => {
        if (user && userProfile) {
            loadGlobalSchemas();
        }
    }, [user, userProfile]);

    const loadGlobalSchemas = () => {
        try {
            setLoading(true);
            setError('');

            // Load schemas from globalEvaluationSchemas collection
            const globalSchemasQuery = query(
                collection(db, 'globalEvaluationSchemas'),
                where('isActive', '==', true),
                orderBy('createdAt', 'desc')
            );

            // Load global schemas from evaluationSchemas collection (created by global admins)
            const adminGlobalSchemasQuery = query(
                collection(db, 'evaluationSchemas'),
                where('isGlobal', '==', true),
                where('isActive', '==', true),
                orderBy('createdAt', 'desc')
            );

            let unsubscribeAdminGlobal: Unsubscribe = () => { };

            const unsubscribeGlobal = onSnapshot(globalSchemasQuery, (globalSnapshot) => {
                const globalSchemasList: EvaluationSchema[] = [];
                globalSnapshot.forEach((doc) => {
                    globalSchemasList.push({ id: doc.id, ...(doc.data() as any), _collection: 'globalEvaluationSchemas' } as EvaluationSchema);
                });

                unsubscribeAdminGlobal = onSnapshot(adminGlobalSchemasQuery, (adminSnapshot) => {
                    const adminGlobalSchemas: EvaluationSchema[] = [];
                    adminSnapshot.forEach((doc) => {
                        adminGlobalSchemas.push({ id: doc.id, ...(doc.data() as any), _collection: 'evaluationSchemas' } as EvaluationSchema);
                    });

                    // Combine and sort by creation date
                    const allSchemas = [...globalSchemasList, ...adminGlobalSchemas].sort((a, b) => {
                        const aTime = a.createdAt?.seconds || 0;
                        const bTime = b.createdAt?.seconds || 0;
                        return bTime - aTime;
                    });

                    setGlobalSchemas(allSchemas);
                    setLoading(false);
                }, (error) => {
                    console.error('Error loading admin global schemas:', error);
                    handleFirestoreError(error, 'Loading admin global schemas', setError);
                    setLoading(false);
                });
            }, (error) => {
                console.error('Error loading global schemas:', error);
                handleFirestoreError(error, 'Loading global schemas', setError);
                setLoading(false);
            });

            return () => {
                unsubscribeGlobal();
                unsubscribeAdminGlobal();
            };
        } catch (error) {
            console.error('Error setting up global schemas listener:', error);
            setError('Failed to load global framework library');
            setLoading(false);
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, schema: EvaluationSchema) => {
        setMenuAnchor(event.currentTarget);
        setSelectedSchema(schema);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setSelectedSchema(null);
    };

    const handleViewSchema = () => {
        if (selectedSchema) {
            // Navigate to a read-only view of the global schema
            const isFromGlobalCollection = selectedSchema._collection === 'globalEvaluationSchemas';
            const viewPath = isFromGlobalCollection
                ? `/evaluation-schemas/global/${selectedSchema.id}`
                : `/evaluation-schemas/${selectedSchema.id}`;
            navigate(viewPath);
        }
        handleMenuClose();
    };

    const handleCloneSchema = () => {
        if (selectedSchema) {
            setSchemaToClone(selectedSchema);
            setCloneName(`${selectedSchema.name} (Copy)`);
            setCloneDialogOpen(true);
        }
        handleMenuClose();
    };

    const handleConfirmClone = async () => {
        if (!schemaToClone || !cloneName.trim() || !user) return;

        try {
            setCloning(true);
            setError('');

            // Create a copy of the schema for the current tenant
            const clonedSchema: any = {
                ...schemaToClone,
                name: cloneName.trim(),
                isGlobal: false,
                isDefault: false,
                tenantId: user?.tenantId,
                sourceSchemaId: schemaToClone.id,
                sourceSchemaName: schemaToClone.name,
                sourceCollection: schemaToClone._collection || 'evaluationSchemas',
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
                createdBy: {
                    uid: user.uid,
                    email: user.email,
                    displayName: userProfile?.displayName || user.email
                },
                updatedBy: {
                    uid: user.uid,
                    email: user.email,
                    displayName: userProfile?.displayName || user.email
                }
            };

            // Remove the id and _collection fields from the cloned schema
            delete clonedSchema.id;
            delete clonedSchema._collection;

            await addDoc(collection(db, 'evaluationSchemas'), clonedSchema);

            setCloneDialogOpen(false);
            setCloneName('');
            setSchemaToClone(null);

            // Navigate back to frameworks list to see the new cloned framework
            navigate('/evaluation-schemas');
        } catch (error) {
            console.error('Error cloning schema:', error);
            handleFirestoreError(error, 'Cloning evaluation framework', setError);
        } finally {
            setCloning(false);
        }
    };

    const handleGenerateSuccess = (generatedSchema: any) => {
        // Navigate to new global schema page
        navigate('/evaluation-schemas/global/new', { state: { generatedSchema } });
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return '';
        return new Date(timestamp.seconds * 1000).toLocaleDateString();
    };

    const getCategoryCount = (schema: EvaluationSchema) => {
        return schema.categories ? schema.categories.length : 0;
    };

    const getCriteriaCount = (schema: EvaluationSchema) => {
        if (!schema.categories) return 0;
        return schema.categories.reduce((total, category) => {
            return total + (category.criteria ? category.criteria.length : 0);
        }, 0);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/evaluation-schemas')}
                    >
                        Back to Frameworks
                    </Button>
                    <Box>
                        <Typography variant="h4" gutterBottom>
                            Global Framework Library
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Browse and clone global evaluation framework templates. You can customize them after cloning to match your specific needs.
                        </Typography>
                    </Box>
                </Box>
                {isGlobalAdmin() && (
                    <Button
                        variant="outlined"
                        startIcon={<AutoAwesome />}
                        onClick={() => setGenerateDialogOpen(true)}
                        sx={{
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                                borderColor: 'primary.dark',
                                backgroundColor: 'rgba(25, 118, 210, 0.04)'
                            }
                        }}
                    >
                        Generate Template
                    </Button>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Global Schema List */}
            {globalSchemas.length === 0 ? (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <Avatar sx={{ bgcolor: 'grey.100', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                        <LocalLibrary />
                    </Avatar>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No global frameworks available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Global administrators haven't published any evaluation framework templates yet.
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {globalSchemas.map((schema) => (
                        <Grid size={{ xs: 12, md: 6, lg: 4 }} key={schema.id}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 600,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    mb: 1
                                                }}
                                            >
                                                {schema.name}
                                            </Typography>
                                            {schema.description && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        mb: 2
                                                    }}
                                                >
                                                    {schema.description}
                                                </Typography>
                                            )}
                                        </Box>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleMenuOpen(e, schema)}
                                        >
                                            <MoreVert />
                                        </IconButton>
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                        <Chip
                                            label={`${getCategoryCount(schema)} Categories`}
                                            size="small"
                                            variant="outlined"
                                        />
                                        <Chip
                                            label={`${getCriteriaCount(schema)} Criteria`}
                                            size="small"
                                            variant="outlined"
                                        />
                                        <Chip
                                            label="Global"
                                            size="small"
                                            color="info"
                                            variant="outlined"
                                        />
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Version: {schema.version || '1.0'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDate(schema.createdAt)}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Action Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleViewSchema}>
                    <ListItemIcon>
                        <Visibility fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>View Details</ListItemText>
                </MenuItem>

                {(isTenantAdmin() || isGlobalAdmin()) && (
                    <MenuItem onClick={handleCloneSchema}>
                        <ListItemIcon>
                            <FileCopy fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Clone to My Tenant</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            {/* Clone Confirmation Dialog */}
            <Dialog open={cloneDialogOpen} onClose={() => { setCloneDialogOpen(false); setSchemaToClone(null); }} maxWidth="sm" fullWidth>
                <DialogTitle>Clone Evaluation Framework</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 3 }}>
                        Clone "{schemaToClone?.name}" to create a customizable copy for your tenant.
                    </Typography>
                    <TextField
                        fullWidth
                        label="New Framework Name"
                        value={cloneName}
                        onChange={(e) => setCloneName(e.target.value)}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setCloneDialogOpen(false); setSchemaToClone(null); }}>Cancel</Button>
                    <Button
                        onClick={handleConfirmClone}
                        variant="contained"
                        disabled={!cloneName.trim() || cloning}
                        startIcon={cloning ? <CircularProgress size={16} /> : <FileCopy />}
                    >
                        {cloning ? 'Cloning...' : 'Clone Framework'}
                    </Button>
                </DialogActions>
            </Dialog>

            <GenerateSchemaDialog
                open={generateDialogOpen}
                onClose={() => setGenerateDialogOpen(false)}
                onSuccess={handleGenerateSuccess}
                isGlobalMode={true}
            />
        </Box >
    );
};

export default GlobalSchemaLibrary;
